// src/pages/Admin/Adherents/AdherentPassageCeinture.jsx
import React, { useEffect, useMemo, useState } from "react";
import { getBeltHistoryByAdherent } from "../../../services/beltServices";
import ButtonDiplome from "../../../components/Button/ButtonDiplome";

export default function AdherentPassageCeinture({ adherentId }) {
  const [ceintures, setCeintures] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!adherentId) return;
    (async () => {
      try {
        setLoading(true);
        const data = await getBeltHistoryByAdherent(adherentId);
        setCeintures(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error("Erreur lors de la récupération des ceintures:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [adherentId]);

  const rows = useMemo(() => {
    // tri du plus récent au plus ancien
    return [...ceintures].sort((a, b) => {
      const da = new Date(a?.date_passage || 0).getTime();
      const db = new Date(b?.date_passage || 0).getTime();
      return db - da;
    });
  }, [ceintures]);

  const beltImageSrc = (beltColor) => {
    const url = beltColor?.belt_url;
    return url ? `/img/belt/${url}` : "/img/belt/ceinture-inconnue.png";
  };

  return (
    <section className="rounded-2xl overflow-hidden ring-1 ring-slate-200/70 dark:ring-slate-700/70 bg-white/90 dark:bg-slate-900/60 backdrop-blur">
      {/* En-tête harmonisé */}
      <div className="px-5 pt-5 sm:px-6">
        <h3 className="text-base sm:text-lg font-semibold text-slate-900 dark:text-slate-100">
          Passages de ceintures
        </h3>
        <div className="mt-2 h-1 w-28 rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500" />
      </div>

      {/* Contenu */}
      <div className="px-5 pb-6 sm:px-6 sm:pb-6 mt-4">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="animate-pulse rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-white/60 dark:bg-slate-900/40 h-40"
              />
            ))}
          </div>
        ) : rows.length === 0 ? (
          <div className="rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 p-5 bg-slate-50 dark:bg-slate-900/40 text-sm text-slate-700 dark:text-slate-300">
            Aucun historique de passage de ceinture disponible.
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {rows.map((c, idx) => {
              const belt = c?.BeltColor;
              const beltName = belt?.nom || "Inconnu";
              const dateStr = c?.date_passage
                ? new Date(c.date_passage).toLocaleDateString("fr-FR")
                : "—";
              const ordre = typeof belt?.ordre === "number" ? belt.ordre : null;
              const showDiplome = ordre !== null && ordre >= 2 && ordre <= 13;

              return (
                <div
                  key={idx}
                  className="relative rounded-xl ring-1 ring-slate-200 dark:ring-slate-800 bg-slate-50 dark:bg-slate-900/40 overflow-hidden shadow-sm hover:shadow transition-shadow"
                >
                  {/* Image avec overlay au survol */}
                  <div className="relative w-full h-16 bg-white dark:bg-slate-800">
                    <img
                      src={beltImageSrc(belt)}
                      alt={`Ceinture ${beltName}`}
                      className="w-full h-16 object-contain"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white text-sm font-semibold">
                        {beltName}
                      </span>
                    </div>
                  </div>

                  {/* Infos */}
                  <div className="px-3 py-3">
                    <div className="flex items-center justify-between">
                      <span className="inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-semibold bg-violet-100 text-violet-800 ring-1 ring-violet-200 dark:bg-violet-900/40 dark:text-violet-200 dark:ring-violet-800/50">
                        {beltName}
                      </span>
                      <span className="text-xs text-slate-600 dark:text-slate-300">
                        {dateStr}
                      </span>
                    </div>

                    {/* Diplôme */}
                    {showDiplome && (
                      <div className="mt-3 flex justify-center">
                        <ButtonDiplome
                          adherentId={adherentId}
                          datePassage={c.date_passage}
                          beltImage={belt?.belt_url}
                          beltName={beltName}
                          adherentNom={c?.Adherent?.nom}
                          adherentPrenom={c?.Adherent?.prenom}
                          dateNaissance={c?.Adherent?.date_naissance}
                        />
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
